/**
 * Availability slot picker (DNKADM-0061 U2) — the shared date-range → returned-slots
 * picker used by create-on-behalf, edit-time and reschedule (handoff §7). It POSTs
 * `getMemberAvailability(contactId, { serviceId, variationId, locationId, startDate,
 * endDate })` — the ADMIN route, read for the TARGET member (DNKADM-0390 U1) — and renders
 * **only the returned slots** (the owned v2 backend is the availability authority — no
 * client-side slot math). Slot `start`/`end` are ISO-8601-with-offset and are rendered
 * **as-is** (no local-tz reformatting).
 *
 * The picker is the SELECTOR only — it raises the chosen slot's `start` (and, for a
 * `MEMBER_SELECTS` service, the slot's option lists) to the host dialog, which owns the
 * staff/resource selection + the POST. When availability can't be loaded the picker shows
 * the failure with a Try again action and a 12-hour manual time on its chosen date
 * (DNKADM-0390 U2), raised through `onManualStart`; the host still owns the write.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, resolveTokens } from '@/theme';
import { resolveReservationsMaps } from '@/theme/maps/reservations';
import { getMemberAvailability, type Availability, type AvailabilitySlot } from '@/api/availability';
import { useResource } from '@/hooks/useResource';
import { dateLabel, isoClockAsIs, isoWallClock } from '@/utils/clockTime';
import { serverMessage } from '@/api/serverMessage';
import { adminAvailabilityMessage, availabilityCodeMessage } from './reservationsError';
import { ManualStartTime } from './ManualStartTime';
import { addDays, formatApiDate, todayIn } from '@/components/list/listDate';

/**
 * Render an ISO-with-offset instant's clock portion as-is (no tz shift), 12-hour
 * through the shared formatter (DNKADM-0386 U2); the raw string when it cannot be split.
 */
function clockAsIs(iso: string): string {
  return isoClockAsIs(iso) ?? iso;
}

/**
 * DNKADM-0344 U1: how many days the picker shows at once — and therefore how many it
 * fetches (`endDate = startDate + DAYS − 1`). Three, not the previous seven: an operator at
 * the counter reads a short list and moves the From date for anything further out. Change
 * this ONE constant to change both the request window and the rendered day list.
 */
export const SLOT_PICKER_DAYS_PER_VIEW = 3;

/** Copy for a day inside the window that the server returned no slots for. */
export const EMPTY_DAY_COPY = 'No available times';

export interface AvailabilitySlotPickerProps {
  /**
   * The TARGET member's contact id (DNKADM-0390 U1): times are read from
   * `POST /v2/admin/members/{contactId}/availability`, whose access window is that member's,
   * never the staff caller's. `null` = no member chosen yet → a prompt, and NO request.
   */
  contactId: string | null;
  serviceId: string;
  variationId: string;
  locationId: string;
  /**
   * DNKADM-0443 U1: pre-filter the times to one coach (Book on behalf's coach picker).
   * Omitted = any coach; no `staffId` is sent.
   */
  staffId?: string;
  /** The location's IANA zone, for the manual start time's offset (DNKADM-0390 U2); null → device. */
  timeZone?: string | null;
  /**
   * A manually chosen start (DNKADM-0390 U2) — raised when available times could not be
   * loaded and the operator picks a 12-hour time on the chosen date; `null` while incomplete.
   */
  onManualStart?: (iso: string | null) => void;
  /** The currently-selected slot start (ISO with offset), or null. */
  selectedStart: string | null;
  /** Raise the chosen slot (its `start` + the slot itself, for the option lists). */
  onSelect: (slot: AvailabilitySlot) => void;
  /**
   * Open the window on this `YYYY-MM-DD` instead of today (DNKADM-0389 U2 — the board's
   * clicked date). The operator can still move it.
   */
  initialDate?: string;
  /**
   * The clicked wall-clock start (`HH:mm`, DNKADM-0389 U2). Once the window loads, the slot
   * on `initialDate` that starts then (else the nearest within 30 minutes) is selected
   * ONCE as a starting point; availability is still the server's, and the operator can pick
   * any other slot.
   */
  preferredStart?: string;
}

export function AvailabilitySlotPicker({
  contactId,
  serviceId,
  variationId,
  locationId,
  staffId,
  timeZone = null,
  onManualStart,
  selectedStart,
  onSelect,
  initialDate,
  preferredStart,
}: AvailabilitySlotPickerProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const rm = resolveReservationsMaps(theme);

  // The window starts today on the location's calendar (DNKADM-0439: `timeZone` is the
  // location zone; null is the device); the operator moves it with the From date — that is the ONLY
  // navigation (no paginator, no "load more"). Its length is SLOT_PICKER_DAYS_PER_VIEW, which
  // drives both the fetch range (endDate) and the rendered day list below, so what is
  // fetched is exactly what is shown (DNKADM-0344 U1/U2).
  const [startDate, setStartDate] = useState<string>(() => initialDate ?? todayIn(timeZone));
  const endDate = useMemo(() => addDays(startDate, SLOT_PICKER_DAYS_PER_VIEW - 1), [startDate]);

  // The ADMIN route, keyed by the target member (DNKADM-0390 U1): no member, no request —
  // never a fall back to the member route, which reads the caller's own (staff) contact.
  // Changing the member re-runs the read.
  const resource = useResource(
    () =>
      contactId
        ? getMemberAvailability(contactId, {
            serviceId,
            variationId,
            locationId,
            startDate,
            endDate,
            ...(staffId !== undefined && staffId !== '' ? { staffId } : {}),
          })
        : Promise.resolve<Availability | null>(null),
    [contactId, serviceId, variationId, locationId, staffId, startDate, endDate],
  );
  const [manualStart, setManualStart] = useState<string | null>(null);

  // A manual start belongs to the failed read it was picked under. The moment the read
  // re-runs (the From date moved, the member/service changed, Try again) it is dropped and
  // the host told so: otherwise the selector would re-seed the OLD day's time under the new
  // date (a wrong-day booking), or a successful re-read would leave Book enabled on a start
  // no slot shows. The raised ISO always equals the date + time on screen.
  useEffect(() => {
    if (resource.status === 'error' || manualStart === null) return;
    setManualStart(null);
    onManualStart?.(null);
  }, [resource.status, manualStart, onManualStart]);

  const days = useMemo(() => resource.data?.days ?? [], [resource.data]);
  const hasAnySlot = days.some((d) => d.slots.length > 0);

  // The prefill (DNKADM-0389 U2): applied once, only while nothing is selected, only from the
  // slots the server returned for the clicked date — exact start first, else the nearest slot
  // within 30 minutes. A later re-load (the operator moved the window) never re-applies it.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || resource.status !== 'ready' || selectedStart !== null) return;
    if (!initialDate || !preferredStart) return;
    const wanted = isoWallClock(`${initialDate}T${preferredStart}:00`);
    const day = days.find((d) => d.date === initialDate);
    if (!wanted || !day) return;
    const target = wanted.hour * 60 + wanted.minute;
    let best: { slot: AvailabilitySlot; distance: number } | null = null;
    for (const slot of day.slots) {
      const clock = isoWallClock(slot.start);
      if (!clock) continue;
      const distance = Math.abs(clock.hour * 60 + clock.minute - target);
      if (distance <= 30 && (best === null || distance < best.distance)) best = { slot, distance };
    }
    prefilled.current = true;
    if (best) onSelect(best.slot);
  }, [resource.status, days, selectedStart, initialDate, preferredStart, onSelect]);

  // DNKADM-0344: the rendered list is the WINDOW, not the response. dink-api returns only
  // dates with ≥1 slot (AvailabilityEngine drops empty days, and today once its slots are
  // past), so a day the studio is fully booked would otherwise vanish from the list — the
  // operator must see it as an empty day, not infer it from a gap. Every window date renders
  // (server slots when returned, else empty); a returned day OUTSIDE the window (a
  // location-zone vs. client-zone boundary) is still rendered after them — the server's
  // slots are never dropped.
  const viewDays = (() => {
    const byDate = new Map(days.filter((d) => d.date !== null).map((d) => [d.date, d]));
    const window = Array.from({ length: SLOT_PICKER_DAYS_PER_VIEW }, (_, i) =>
      addDays(startDate, i),
    );
    const inWindow = window.map((date) => byDate.get(date) ?? { date, slots: [] });
    const extra = days.filter((d) => d.date === null || !window.includes(d.date));
    return [...inWindow, ...extra];
  })();

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 1.5 }}>
        <TextField
          type="date"
          size="small"
          label="From"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 170 }}
        />
        <Typography sx={{ fontSize: 13, color: t.text2 }}>through {formatApiDate(endDate)}</Typography>
      </Stack>

      {resource.status === 'loading' && (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, py: 2 }} role="status">
          <CircularProgress size={18} />
          <Typography sx={{ color: t.text2 }}>Loading available times…</Typography>
        </Stack>
      )}

      {contactId === null && (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, py: 2, color: t.text2 }} data-testid="slot-picker-no-member">
          <Icon name="person_search" sx={{ fontSize: 22, color: t.textGhost }} />
          <Typography>Pick a member to see available times.</Typography>
        </Stack>
      )}

      {resource.status === 'error' && (
        <>
          <Alert
            severity="warning"
            role="alert"
            sx={{ mb: 1 }}
            action={
              <Button color="inherit" size="small" onClick={resource.refetch}>
                Try again
              </Button>
            }
          >
            {/* The admin route's own failures first (DNKADM-0390 U2: unknown/foreign member,
                forbidden), then the four DNKAPI-0444 availability codes (DNKADM-0289), then
                the server message → generic fallback. */}
            {adminAvailabilityMessage(resource.error) ??
              availabilityCodeMessage(resource.error) ??
              serverMessage(resource.error) ??
              'Available times couldn’t be loaded.'}
          </Alert>
          {/* The manual fallback (DNKADM-0390 U2): a 12-hour time on the chosen date; the ISO
              start carries the location's offset. Replaces the raw ISO text box. Keyed by the
              date so a new day always starts from an empty time. */}
          <ManualStartTime
            key={startDate}
            date={startDate || null}
            timeZone={timeZone}
            value={manualStart}
            onChange={(iso) => {
              setManualStart(iso);
              onManualStart?.(iso);
            }}
          />
        </>
      )}

      {resource.status === 'ready' && contactId !== null && !hasAnySlot && (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, py: 2, color: t.text2 }}>
          <Icon name="event_busy" sx={{ fontSize: 22, color: t.textGhost }} />
          <Typography>No available times in this range. Try another date.</Typography>
        </Stack>
      )}

      {resource.status === 'ready' &&
        hasAnySlot &&
        viewDays.map((day, di) => (
          <Box key={day.date ?? `day-${di}`} sx={{ mb: 1.5 }} data-testid="slot-picker-day">
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: t.textSubtle,
                mb: 0.75,
              }}
            >
              {dateLabel(day.date) ?? day.date ?? '—'}
            </Typography>
            {day.slots.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: t.text2 }}>{EMPTY_DAY_COPY}</Typography>
            ) : (
              <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                {day.slots.map((slot) => {
                  const on = slot.start === selectedStart;
                  const toggle = on ? rm.slotToggle.on : rm.slotToggle.off;
                  return (
                    <Box
                      key={slot.start}
                      component="button"
                      type="button"
                      onClick={() => onSelect(slot)}
                      aria-pressed={on}
                      sx={{
                        minWidth: 88,
                        p: '10px 10px',
                        borderRadius: 1,
                        border: `1px solid ${toggle.border}`,
                        bgcolor: toggle.bg,
                        color: toggle.fg,
                        fontWeight: 600,
                        fontSize: 14,
                        font: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      {clockAsIs(slot.start)}
                      {/* DNKADM-0443 U2: inside the service's booking notice — bookable by
                          staff, but only after an explicit confirm in the host dialog. */}
                      {slot.insideLeadTime && (
                        <Box
                          component="span"
                          sx={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'inherit' }}
                        >
                          Inside notice
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        ))}
    </Box>
  );
}
